import { Clock, Eye, EyeOff } from "lucide-react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";

// Common timezones (can be expanded)
const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)" },
  { value: "Australia/Sydney", label: "Sydney (AEDT)" },
];

export function SettingsPanel() {
  const [showFocusedModeDialog, setShowFocusedModeDialog] = useState(false);
  const [pendingFocusedMode, setPendingFocusedMode] = useState(false);
  
  // User preferences (would be stored in user_profiles table)
  const [focusedMode, setFocusedMode] = useState(() => {
    const stored = localStorage.getItem('ui_focused_mode');
    return stored ? JSON.parse(stored) : false;
  });
  
  const [timeFormat, setTimeFormat] = useState(() => {
    return localStorage.getItem('ui_time_format') || '12h';
  });
  
  const [timezone, setTimezone] = useState(() => {
    return localStorage.getItem('ui_timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone;
  });

  const handleFocusedModeToggle = (enabled: boolean) => {
    if (enabled) {
      // Show confirmation dialog
      setPendingFocusedMode(true);
      setShowFocusedModeDialog(true);
    } else {
      // Turning off, no confirmation needed
      applyFocusedMode(false);
    }
  };

  const applyFocusedMode = (enabled: boolean) => {
    setFocusedMode(enabled);
    localStorage.setItem('ui_focused_mode', JSON.stringify(enabled));
    // Dispatch event so other components can react
    window.dispatchEvent(new CustomEvent('focusedModeChange', { detail: enabled }));
  };

  const handleConfirmFocusedMode = () => {
    applyFocusedMode(true);
    setShowFocusedModeDialog(false);
    
    // Apply UI changes immediately
    // These events will be picked up by the relevant components
    window.dispatchEvent(new CustomEvent('applyFocusedMode', { 
      detail: {
        collapseKnowledgeBar: true,
        closeHistoryPanel: true,
        hideOriginalAnalysis: true,
        hideAnalysisAnnotations: true,
        hideOriginalAnnotations: true,
        collapseSideBrain: true,
        hideTimelineFilters: true
      }
    }));
  };

  const handleTimeFormatChange = (format: string) => {
    setTimeFormat(format);
    localStorage.setItem('ui_time_format', format);
    window.dispatchEvent(new CustomEvent('timeFormatChange', { detail: format }));
  };

  const handleTimezoneChange = (tz: string) => {
    setTimezone(tz);
    localStorage.setItem('ui_timezone', tz);
    window.dispatchEvent(new CustomEvent('timezoneChange', { detail: tz }));
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Settings</h2>
        <p className="text-sm text-muted-foreground">Customize your experience</p>
      </div>

      {/* Focused Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {focusedMode ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            Focused Mode
          </CardTitle>
          <CardDescription>
            Minimalist interface with only essential panels visible by default
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <Label htmlFor="focused-mode" className="text-sm font-normal">
              Enable Focused Mode
            </Label>
            <Switch
              id="focused-mode"
              checked={focusedMode}
              onCheckedChange={handleFocusedModeToggle}
            />
          </div>
          
          {focusedMode && (
            <div className="mt-4 p-3 bg-muted/50 rounded-md text-xs space-y-1">
              <p className="font-medium">Currently active:</p>
              <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                <li>Knowledge Bar starts collapsed</li>
                <li>History panel starts closed</li>
                <li>Only Current analysis panel shows (Original hidden)</li>
                <li>Only Current annotations show</li>
                <li>Side Brain panels start collapsed</li>
                <li>Timeline filters hidden by default</li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* Time & Date Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-4 w-4" />
            Time & Date
          </CardTitle>
          <CardDescription>
            Configure how times and dates are displayed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Time Format */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Time Format</Label>
            <RadioGroup value={timeFormat} onValueChange={handleTimeFormatChange}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="12h" id="12h" />
                <Label htmlFor="12h" className="font-normal cursor-pointer">
                  12-hour (2:30 PM)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="24h" id="24h" />
                <Label htmlFor="24h" className="font-normal cursor-pointer">
                  24-hour (14:30)
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label htmlFor="timezone" className="text-sm font-medium">
              Timezone
            </Label>
            <Select value={timezone} onValueChange={handleTimezoneChange}>
              <SelectTrigger id="timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Focused Mode Confirmation Dialog */}
      <AlertDialog open={showFocusedModeDialog} onOpenChange={setShowFocusedModeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable Focused Mode?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This will minimize the interface to reduce distractions:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Knowledge Bar will collapse</li>
                <li>History panel will close</li>
                <li>Only Current analysis panel will show</li>
                <li>Only Current annotations will show</li>
                <li>Side Brain panels will collapse</li>
              </ul>
              <p className="pt-2">Would you like to apply these changes now?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowFocusedModeDialog(false);
              setPendingFocusedMode(false);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmFocusedMode}>
              Enable Focused Mode
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
